import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '../../services';
import { useUser } from '../../context/UserContext';
import {
  AdminModal, AdminPageHeader, Btn, DataTable, DetailCard, DropChip, Field, FilterRow, Pill, SearchField,
  money, shortDate, Loading, textareaClass, useAdminShell,
} from '../../components/admin/shell';

const STATUSES = ['draft', 'published', 'ended'];
const STATUS_OPTIONS = [{ value: '', label: 'ALL' }, ...STATUSES.map((s) => ({ value: s, label: s.toUpperCase() }))];
const ENTRY_OPTIONS = [{ value: '', label: 'ALL' }, { value: 'native', label: 'NATIVE' }, { value: 'legacy', label: 'PASS EVENT' }, { value: 'none', label: 'NO ENTRY' }];
const word = (s) => String(s || '—').replace(/_/g, ' ').toUpperCase();
const entryWord = (g) => (g.entry === 'native' ? `NATIVE · ${word(g.lifecycle)}` : g.entry === 'legacy' ? 'PASS EVENT' : 'NO ENTRY');
const cancellable = (g) => g && g.status !== 'ended' && !['ended', 'canceled', 'dissolved'].includes(g.lifecycle || '');

/**
 * EVENTS › GATHERINGS — every gathering with its entry ownership (native lifecycle or the
 * legacy pass event), who hosts it, what entry collected. CANCEL (row and detail) is
 * `gatherings` · cancel; the detail panel is the nested route /admin/gatherings/:id.
 */
export default function GatheringsAdminPage() {
  const { can } = useUser();
  const { openPerson, canSeePeople } = useAdminShell();
  const navigate = useNavigate();
  const { id: selectedId } = useParams();
  const canCancel = can('gatherings', 'cancel');
  const [status, setStatus] = useState('');
  const [entry, setEntry] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [detailKey, setDetailKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setApplied(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getGatherings(page, { status, entry, search: applied });
    setLoading(false);
    if (r.success) { setRows(r.data.gatherings || []); setTotal(r.data.total || 0); }
  }, [page, status, entry, applied]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <AdminPageHeader section="gatherings" meta="Every gathering and who owns its entry. Cancelling closes entry and refunds every paid entry; the events on the agenda are untouched." />
      <FilterRow>
        <DropChip label="STATUS" value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={STATUS_OPTIONS} />
        <DropChip label="ENTRY" value={entry} onChange={(v) => { setEntry(v); setPage(1); }} options={ENTRY_OPTIONS} />
        <SearchField value={search} onChange={setSearch} placeholder="SEARCH NAME" />
      </FilterRow>
      {selectedId && (
        <GatheringDetailPanel key={`${selectedId}:${detailKey}`} id={selectedId} onBack={() => navigate('/admin/gatherings')} onCancel={(g) => setCancelTarget(g)} />
      )}
      <DataTable
          columns={[
            { key: 'name', label: 'NAME', fr: 2 },
            {
              key: 'hostDisplayName',
              label: 'HOST',
              fr: 1.3,
              render: (g) => (
                <span
                  className={canSeePeople && g.hostUserId ? 'cursor-pointer hover:underline' : ''}
                  onClick={canSeePeople && g.hostUserId ? (e) => { e.stopPropagation(); openPerson(g.hostUserId); } : undefined}
                >
                  {g.hostDisplayName || g.hostEmail || '—'}
                </span>
              ),
            },
            { key: 'status', label: 'STATUS', fr: 0.8, card: 'badge', render: (g) => <Pill tone={g.status === 'canceled' ? 'bad' : g.status === 'ended' ? 'neutral' : 'good'}>{word(g.status)}</Pill> },
            { key: 'entry', label: 'ENTRY', fr: 1.2, render: entryWord },
            { key: 'entryFeeCents', label: 'FEE', fr: 0.7, right: true, render: (g) => (g.entryFeeCents ? money(g.entryFeeCents) : '—') },
            { key: 'entryCount', label: 'IN', fr: 0.5, right: true },
            { key: 'collectedCents', label: 'COLLECTED', fr: 0.9, right: true, render: (g) => money(g.collectedCents) },
            { key: 'eventCount', label: 'EVENTS', fr: 0.6, right: true },
            { key: 'startsAt', label: 'STARTS', fr: 0.9, render: (g) => shortDate(g.startsAt) },
          ]}
          rows={rows}
          loading={loading}
          onRowClick={(g) => navigate(`/admin/gatherings/${g.id}`)}
          hasActions={canCancel}
          actions={(g) => (canCancel && cancellable(g) ? [{ label: 'CANCEL', variant: 'danger', onClick: () => setCancelTarget(g) }] : [])}
          selected={(g) => String(g.id) === String(selectedId)}
          total={total}
          page={page}
          pageSize={20}
          onPage={setPage}
        />
      <CancelGatheringModal gathering={cancelTarget} onClose={() => setCancelTarget(null)} onCanceled={() => { setCancelTarget(null); load(); setDetailKey((k) => k + 1); }} />
    </>
  );
}

function CancelGatheringModal({ gathering, onClose, onCanceled }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(''); setError(''); }, [gathering]);
  const confirm = async () => {
    setBusy(true);
    const r = await adminService.cancelGathering(gathering.id, reason.trim());
    setBusy(false);
    if (r.success) onCanceled(); else setError(r.error || 'Could not cancel the gathering.');
  };
  if (!gathering) return null;
  return (
    <AdminModal
      open
      onClose={onClose}
      title="CANCEL GATHERING"
      kicker="Entry closes and every paid entry is refunded to where it came from. The events on the agenda keep running — cancel those from EVENTS."
      footer={(
        <>
          <Btn size="sheet" onClick={onClose}>KEEP IT</Btn>
          <Btn size="sheet" variant="red" onClick={confirm} disabled={busy} data-testid="confirm-cancel-gathering">{busy ? 'CANCELING…' : 'CANCEL GATHERING'}</Btn>
        </>
      )}
    >
      <Field label="REASON (OPTIONAL)" error={error}>
        <textarea id="cancel-gathering-reason" aria-label="REASON (OPTIONAL)" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for cancellation…" rows={2} className={`resize-none ${textareaClass}`} />
      </Field>
    </AdminModal>
  );
}

function GatheringDetailPanel({ id, onBack, onCancel }) {
  const panelRef = useRef(null);
  useEffect(() => { panelRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [id]);
  const { can } = useUser();
  const { openPerson, canSeePeople } = useAdminShell();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await adminService.getGatheringDetail(id);
      if (cancelled) return;
      setData(r.success ? r.data : null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="rounded-[24px] bg-white p-6"><Loading /></div>;
  if (!data) return <div className="rounded-[24px] bg-white p-6 font-mono text-[11px] text-[#DC2626]">COULD NOT LOAD THIS GATHERING.</div>;
  const { gathering: g, host, entry, state, money: m, admissions = [], legacyApplicants = [], events = [], disputes = [] } = data;
  const row = { ...g, entry, lifecycle: state?.status };
  const info = [
    ['DATES', `${shortDate(g.startsAt)} → ${shortDate(g.endsAt)}`],
    ['ENTRY', entry === 'native' ? `NATIVE · ${word(state?.status)}` : entry === 'legacy' ? `PASS EVENT ${g.passEventId}` : 'NO ENTRY'],
    ['MODE', `${word(g.mode)} · ${word(g.fundingType)}`],
    ['ENTRY FEE', g.entryFeeCents ? money(g.entryFeeCents) : '—'],
    ['COLLECTED', money(m?.collectedCents)],
    ['REFUNDED', money(m?.refundedCents)],
    ['FROZEN', money(m?.frozenCents)],
    ['IN ESCROW', money(m?.heldCents)],
    ['RELEASED', money(m?.releasedCents)],
    ['RELEASE', state?.escrowReleasedAt ? `RELEASED ${shortDate(state.escrowReleasedAt)}` : state?.escrowReleaseAt ? `DUE ${shortDate(state.escrowReleaseAt)}` : '—'],
    ['ENTRIES', m ? `${m.paidCount} IN · ${m.acceptedCount} ACCEPTED · ${m.pendingCount} WAITING · ${m.refundedCount} REFUNDED` : '—'],
    ['MIGRATION', state ? `${state.backfilledAt ? 'BACKFILLED' : 'NOT BACKFILLED'} · ${state.reconciledAt ? 'RECONCILED' : 'NOT RECONCILED'}` : '—'],
  ].map(([label, value]) => ({ label, value }));
  const people = entry === 'native' ? admissions.map((a) => ({ id: a.id, userId: a.userId, avatarUrl: a.avatarUrl, name: a.displayName, status: a.status, cents: a.principalCents, qty: a.quantity, refunded: a.refunded, frozen: a.isFrozen, when: a.createdAt, checkedIn: a.ticketsCheckedIn, seats: a.ticketCount }))
    : legacyApplicants.map((a) => ({ id: a.admissionId || a.userId, userId: a.userId, avatarUrl: a.avatarUrl, name: a.displayName, status: a.status, cents: a.principalCents, qty: a.quantity, refunded: a.refunded, frozen: a.isFrozen, when: a.appliedAt, checkedIn: null, seats: null }));

  return (
    <section ref={panelRef} className="flex flex-col gap-[14px]" data-testid="gathering-admin-detail">
      <DetailCard
        statusLabel={word(g.status)}
        statusTone={g.status === 'canceled' ? 'bad' : g.status === 'ended' ? 'neutral' : 'good'}
        kicker={entryWord(row)}
        title={g.name}
        sub={host ? `RUN BY ${(host.displayName || host.email || '').toUpperCase()}` : null}
        info={host && canSeePeople
          ? [{ label: 'HOST', value: (host.displayName || host.email || '—').toUpperCase(), onClick: () => openPerson(host.id) }, ...info]
          : info}
        actions={(
          <>
            <Btn variant="white" size="card" onClick={() => window.open(`/gatherings/${g.id}`, '_blank', 'noopener')}>OPEN GATHERING ↗</Btn>
            {can('gatherings', 'cancel') && cancellable(row) && <Btn variant="danger" size="card" onClick={() => onCancel(row)} data-testid="detail-cancel-gathering">CANCEL GATHERING</Btn>}
            <Btn variant="quiet" size="card" onClick={onBack}>BACK</Btn>
          </>
        )}
      />
      <DataTable
        title={`ENTRY (${people.length})`}
        right={canSeePeople ? 'A ROW OPENS THAT PERSON' : null}
        onRowClick={canSeePeople ? (a) => a.userId && openPerson(a.userId) : undefined}
        rowImage={(a) => a.avatarUrl}
        imageRadius={999}
        columns={[
          { key: 'name', label: 'NAME', fr: 1.6, render: (a) => a.name || '—' },
          { key: 'status', label: 'STATUS', fr: 1, card: 'badge', render: (a) => <Pill tone={a.frozen ? 'warn' : a.status === 'paid' ? 'good' : 'neutral'}>{word(a.status)}{a.frozen ? ' · FROZEN' : ''}</Pill> },
          { key: 'cents', label: 'PAID', fr: 0.8, right: true, render: (a) => `${money(a.cents)}${a.refunded ? ' (R)' : ''}` },
          { key: 'seats', label: 'SEATS', fr: 0.7, render: (a) => (a.seats == null ? '—' : `${a.checkedIn}/${a.seats} in`) },
          { key: 'when', label: 'WHEN', fr: 0.7, render: (a) => shortDate(a.when) },
        ]}
        rows={people}
        empty="NO ENTRIES."
      />
      <DataTable
        title={`AGENDA (${events.length})`}
        right="EACH EVENT'S OWN MONEY LIVES UNDER EVENTS"
        columns={[
          { key: 'title', label: 'EVENT', fr: 2 },
          { key: 'dateTime', label: 'WHEN', fr: 0.9, render: (e) => shortDate(e.dateTime) },
          { key: 'status', label: 'STATUS', fr: 0.8, card: 'badge', render: (e) => <Pill>{word(e.status)}</Pill> },
          { key: 'ticketPriceCents', label: 'PRICE', fr: 0.7, right: true, render: (e) => (e.ticketPriceCents ? money(e.ticketPriceCents) : 'FREE') },
        ]}
        rows={events}
        hasActions
        actions={(e) => [{ label: 'OPEN IN EVENTS', onClick: () => window.location.assign(`/admin/events/${e.id}`) }]}
        empty="NO EVENTS ON THE AGENDA."
      />
      <DataTable
        title={`STRIPE DISPUTES ON ENTRY (${disputes.length})`}
        columns={[
          { key: 'stripeDisputeId', label: 'DISPUTE', fr: 1.6, render: (d) => <span data-testid="gathering-dispute">{d.stripeDisputeId}</span> },
          { key: 'status', label: 'STATUS', fr: 0.8, card: 'badge', render: (d) => <Pill tone={d.resolvedAt ? 'neutral' : 'bad'}>{word(d.status)}</Pill> },
          { key: 'amountCents', label: 'AMOUNT', fr: 0.8, right: true, render: (d) => money(d.amountCents) },
          { key: 'openedAt', label: 'WHEN', fr: 1, render: (d) => `${shortDate(d.openedAt)}${d.resolvedAt ? ` → ${shortDate(d.resolvedAt)}` : ' · OPEN'}` },
        ]}
        rows={disputes}
        rowKey={(d) => d.id}
        empty="NO CHARGEBACKS ON THIS GATHERING'S ENTRY."
      />
    </section>
  );

}
